import { compose } from '@progress/kendo-data-query/dist/npm/funcs';
import React from 'react';
import Component from '../../modules/component/Component';
import {
  matchPath, 
  Redirect,
  withRouter
} from 'react-router-dom';
import i18n from '../../../locales/i18n';
import { APP_SETTINGS, AuthRoutesConfig } from '../services/AuthSettings';
import LoginPage from '../../../components/login_page/LoginPage';
import {
  SignOut,
  SignedOutCallback,
  SignIn,
  SignedInCallback,
  signInSilentCallback
} from '../components';
import { IAuthConfig } from '../interfaces/AuthInterface';
import AuthService from '../services/AuthService';
import { Position, Dispatcher } from '../../../index';
import _ from 'lodash';
import Translatable from '../../services/i18n/Translatable';
import AuthRouter from '../../../components/router/AuthRouter';
import { ROUTE_PREFIX } from '../../../components/router/Route';
import Configuration from '../../config/Configuration';
import Mask from '../../modules/mask/Mask';
import { AUTH_PROVIDER_PROPS, AUTH_PROVIDER_STATE } from './Types';

/* istanbul ignore next */
export const AuthContext = React.createContext({
  signIn: () => ({}),
  signInRedirectCallback: () => ({}),
  signInSilentCallback: () => ({}),
  signOutRequest: () => ({}),
  signedOutRedirectCallback: () => ({}),
  componentDidMount: () => ({}),
  isAuthenticated: () => ({})
});

export const AuthConsumer = AuthContext.Consumer;

/**
 * A list of auth route components
 */
export const AuthRoutesComponents = {
  post_logout_redirect_uri: {
    component: SignedOutCallback
  },
  silent_redirect_uri: {
    component: signInSilentCallback
  },
  redirect_uri: {
    component: SignedInCallback
  },
  login_uri: {
    component: <LoginPage />
  },
  signin_uri: {
    component: SignIn
  },
  logout_uri: {
    component: SignOut
  }
};

// create auth routes list
let AuthRoutes = [];
AuthRoutesConfig.map((route) => {
  route = _.merge(route, AuthRoutesComponents[route.routeKey]);
  AuthRoutes.push(route);
});

class AuthProvider extends Component<AUTH_PROVIDER_PROPS, AUTH_PROVIDER_STATE> {
  public static authConfig: IAuthConfig;

  public state: AUTH_PROVIDER_STATE = {
    initialized: false,
    unauthorized: [null]
  };

  constructor(props: AUTH_PROVIDER_PROPS) {
    super(props);
    AuthService.registerServiceToDispatcher();
  }

  /**
   * Creates Authentication consumer
   * @param {Function} callbackFn
   * @param {string} localeKey Localization key
   */
  public static createConsumer(callbackFn: string, localeKey: string): any {
    let locale = process.env.NODE_ENV === 'production' ? localeKey : `translation${Translatable.namespaceSeparator}${localeKey}`;
    return (
      <AuthConsumer key={localeKey}>
        {
          /* istanbul ignore next */
          () => {
            // these routes will go before any other actions
            // as they are used for auth
            Dispatcher.dispatch(callbackFn, Position.start);
            return <Mask message={i18n.t(locale)} />;
          }
        }
      </AuthConsumer>
    );
  }

  /**
   * Returns `true` if path is whitelisted
   * All authentication paths are whitelisted
   */
  public static isAuthPath = () => {
    const currentPathName = window.location.pathname;
    const isWhitelisted = AuthRoutesConfig.filter((item: any) => matchPath(currentPathName, item)).length;
    return !!isWhitelisted;
  };

  runProvider = () => {
    let state = {};
    return AuthService.isAuthenticated()
      .then((authenticated) => {
        state = {
          initialized: true
        };

        let toCfg = {
          pathname: APP_SETTINGS.login_uri,
          state: { 
            route: Configuration.getPathname()
          } // writes last route into history state
        };

        if (!authenticated) {
          state = _.assign(state, {
            unauthorized: [<Redirect
              key={ROUTE_PREFIX + 'redirect-login-url'}
              exact={true}
              from={'/'}
              to={toCfg}
            />]
          });
        }
        this.setState(state);
      });
  };

  renderBody() {
    const { initialized, unauthorized } = this.state;
    let component: any = null;
    if (!initialized) {
      component = (
        <Mask
          message={this.translate('loadingApplication')}
          componentDidMount={() => this.dispatch('runAuthProvider')}
        />
      );
    } else {
      component = (
        <AuthContext.Provider value={AuthService as any}>
          {/* Render AUTH navigation first */}
          <AuthRouter
            key={'AuthNavigation'}
            config={AuthRoutes}
            children={unauthorized}
            defaultRoute={APP_SETTINGS.landing_uri}
          />

          {/* Render children/sub navigation second */}
          {this.props.children}
        </AuthContext.Provider>
      );
    }
    return component;
  }
}

const enhance = compose(withRouter);

export default enhance(AuthProvider);
